import getAdditionalHeaderContent from '~/Layouts/BundlePage/BundlePageHeader/content';
import styles from './BundleInfo.scss';
import { PresentationStyles, assets, items } from '@wg/wows-entities/const';
import WoWSEntity from '@wg/wows-entities/wrappers/react/WoWSEntity';
import MainFeaturesLayout from '~/Layouts/Features/Views/MainFeaturesLayout/MainFeaturesLayout';
import { t } from '~/utils/localization';
import classNames from 'classnames';
import { useAppSelector } from '~/Store';
import equal from 'fast-deep-equal/react';
import Button, { ButtonVariant } from '~/components/Button/Button';
import PortPreviewButton from '~/components/PortPreviewButton/PortPreviewButton';
import { useDispatch } from 'react-redux';
import { appActions } from '~/Store/appSlice';
import { POPUPS_NAME } from '~/components/PopupManager';
import { openPort } from '~/api/WoWsClient';
import { PortPreviewType } from '~/components/Port/settings';
import { isInGameBrowser, redirectToLogin } from '~/utils/utils';
import PurchaseProcessor from '~/processors/PurchaseProcessor';
import { getCompensationForPrimaryItem, isFreeBundle } from '~/utils/bundles';
import Account from '~/account/Account';
import { useMemo } from 'react';
import Info from '~/components/Info/Info';
import { DivTooltip } from '@wg/wows-react-uikit';
import useAlreadyPurchased from '~/hooks/useAlreadyPurchased';
import Purchased from '~/components/Purchased/Purchased';

interface IBundleInfo {
    bundle: IBundle;
}

const BundleInfo = ({ bundle }: IBundleInfo) => {
    const dispatch = useDispatch();
    const inventory = useAppSelector((state) => state.account.inventory);
    const shipFeatureMap = useAppSelector((state) => state.features.shipFeatureMap, equal);
    const isVehicle = bundle.primaryItem?.type === items.VEHICLES;
    const otherRewards = bundle.entitlements.filter((item) => !item.isPrimary && !item.isHidden && item.type !== items.TOKEN && item.type !== items.DEFAULT_CREW && item.type !== assets.SLOT);
    const isFree = isFreeBundle(bundle);
    const compensation = getCompensationForPrimaryItem(bundle.primaryItem);
    const isAlreadyPurchased = useAlreadyPurchased(bundle);
    const [isRecieved] = useMemo(() => {
        return Account.getInventoryInfoByType(items.VEHICLES, bundle.primaryItem?.identifier);
    }, [inventory, bundle]);

    const onPreview = () => {
        openPort(bundle.primaryItem.identifier, -1, bundle.primaryItem.type, PortPreviewType.DEFAULT, -1, false, true, location.pathname + location.search);
    };

    const showAutodescriptionPopup = (item: IItemCommonData) => {
        if (item.type !== items.LOOTBOX) {
            return;
        }
        dispatch(
            appActions.changeVisibilityPopup({
                name: POPUPS_NAME.ITEM_DESCRIPTION_POPUP,
                isVisible: true,
                data: {
                    item,
                    autoDescriptionSettings: {},
                },
            }),
        );
    };

    const claim = () => {
        if (Account.getAccount()?.id) {
            new PurchaseProcessor(bundle, {
                popupData: {
                    bundleId: bundle.id,
                    fromCategory: true,
                },
            }).showPopup();
        } else {
            redirectToLogin();
        }
    };

    return (
        <div className={styles.wrapper}>
            <div className={classNames(styles.header, styles.blockWrapper)}>
                <div className={styles.title}>
                    <DivTooltip
                        className={styles.weTitle}
                        style={{ cursor: 'pointer' }}
                        fixed
                        position={'center-left'}
                        tooltipBody={
                            <WoWSEntity
                                type={bundle.primaryItem.type}
                                id={bundle.primaryItem.identifier}
                                presentation={{
                                    style: PresentationStyles.TOOLTIP_WIDGET,
                                    withTooltip: true,
                                }}
                                customisation={{
                                    isExistsInInventory: isRecieved,
                                    shipId: bundle.primaryItem.customisation?.shipId,
                                }}
                            />
                        }
                    >
                        <WoWSEntity
                            key={`wows_entity_reward_${bundle.primaryItem.type}_${bundle.primaryItem.identifier}`}
                            type={bundle.primaryItem.type}
                            id={bundle.primaryItem.identifier}
                            presentation={{
                                renderWithoutFade: true,
                                withText: false,
                            }}
                        />
                        <Info className={styles.infoIcon} />
                    </DivTooltip>
                </div>
                <div className={styles.subTitle}>{getAdditionalHeaderContent(bundle)}</div>
            </div>
            {isRecieved && compensation && (
                <div className={classNames(styles.features, styles.blockWrapper)}>
                    <div className={styles.blockTitle}>{t('As you already own this ship, you will be given compensation instead')}</div>
                    <div className={styles.rewardsList}>
                        <div className={styles.rewardItem}>
                            <WoWSEntity
                                key={`wows_entity_compensation_reward_${bundle.primaryItem.type}_${bundle.primaryItem.identifier}`}
                                type={compensation.currency}
                                amount={compensation.amount}
                                presentation={{
                                    renderWithoutFade: true,
                                    withTooltip: true,
                                    style: PresentationStyles.WIDGET,
                                    withText: false,
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
            {isVehicle && !isRecieved && shipFeatureMap[bundle.primaryItem.identifier]?.mainFeatures && (
                <div className={classNames(styles.features, styles.blockWrapper)}>
                    <div className={styles.blockTitle}>{t('Ключевые особенности корабля:')}</div>
                    <MainFeaturesLayout shipId={bundle.primaryItem.identifier} />
                </div>
            )}
            {!!otherRewards.length && (
                <div className={classNames(styles.rewardsWrapper, styles.blockWrapper)}>
                    <div className={styles.blockTitle}>{t('You will also receive these gifts')}</div>
                    <div className={styles.rewardsList}>
                        {otherRewards.map((entity, index) => {
                            return (
                                <div
                                    className={classNames(styles.rewardItem, {
                                        [styles.clickable]: entity.type === items.LOOTBOX,
                                    })}
                                    key={`reward_entity_${entity.type}_${entity.identifier}_${index}`}
                                    onClick={() => {
                                        showAutodescriptionPopup(entity);
                                    }}
                                >
                                    <WoWSEntity
                                        type={entity.type}
                                        id={entity.identifier}
                                        amount={entity.amount}
                                        presentation={{
                                            renderWithoutFade: true,
                                            withTooltip: true,
                                            style: PresentationStyles.WIDGET,
                                            withShortText: true,
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            <div className={classNames(styles.buttonsWrapper, styles.blockWrapper)}>
                {isAlreadyPurchased ? (
                    <Purchased />
                ) : (
                    <>
                        <Button label={isFree ? t('Получить бесплатно') : t('Получить')} variant={ButtonVariant.orange} isFree={isFree} className={styles.claimButton} onClick={claim} />
                        {isVehicle && !isRecieved && isInGameBrowser && <PortPreviewButton onClick={onPreview} />}
                    </>
                )}
            </div>
        </div>
    );
};

export default BundleInfo;
